import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import type { ApiFailure, ApiResult, Schema } from '@/api/client'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { CHANGES, STALE_CHOICES } from './changes'

export type SiteGating = Schema<'GatingOut'>
export type GatingBody = Schema<'GatingIn'>

/**
 * What AugRank may change on a site without asking — S-D5's screen 08.
 *
 * Settings consolidation (2026-09-15): extracted from `pages/seo/ApprovalGating`'s
 * `GatingCard`; rendered by `/settings/approvals` inside a `SettingRow` and by
 * the wizard step full width.
 *
 * **Every toggle renders off unless the API says `true`.** That is not this
 * form being cautious on top of a safe server; it is the same rule stated at
 * both ends, and `app/seo/gating.py` spells out why the comparison is `=== true`
 * rather than truthiness: `approval_config` is a `jsonb` column that can hold a
 * missing key, a null, or the string `"false"`, and every one of those means
 * gated. The design's mock draws the last two toggles pre-checked; its own alert
 * above them says *"Nothing below is on unless you turn it on"*, and the alert is
 * the normative half.
 */
export function ApprovalsForm({
  site,
  onSubmit,
  submitLabel,
}: {
  site: SiteGating
  onSubmit: (body: GatingBody) => Promise<ApiResult<unknown>>
  submitLabel: string
}): JSX.Element {
  const [automatic, setAutomatic] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      CHANGES.map((change) => [change.code, site.automatic[change.code] === true]),
    ),
  )
  const [role, setRole] = useState(site.approver_role)
  const [stale, setStale] = useState(site.stale_after_days)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)

  // SEO-UI-96. **The order is `STALE_CHOICES`', not numeric**, because *never*
  // is 0 and sorting would put it first — which is the drawing's own point about
  // this control: the reminder windows come first and *never expire* is the
  // deliberate end of the list, not its default. A stored value that is not one of
  // the three is inserted among the windows, where it belongs, rather than after
  // the option that means *no window at all*.
  const days = STALE_CHOICES.includes(stale)
    ? STALE_CHOICES
    : [...STALE_CHOICES.filter((option) => option !== 0), stale].sort(ascending).concat(0)

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setFailure(null)
    const result = await onSubmit({ automatic, approver_role: role, stale_after_days: stale })
    setBusy(false)
    if (!result.ok) setFailure(result.failure)
  }

  return (
    <form
      className="app-gating"
      aria-label={`Approvals — ${site.domain}`}
      onSubmit={(event) => void submit(event)}
    >
      <h3 className="ar-h3">What can we do to {site.domain} without asking?</h3>
      <div className="ar-stack">
        <div className="ar-alert ar-alert--info">
          <span className="ar-alert__icon" aria-hidden="true">
            ◷
          </span>
          <div className="ar-alert__body">
            <strong>Everything is approval-gated by default</strong>
            Nothing below is on unless you turn it on. You can change this at any time and it
            applies to the next run, never retroactively.
          </div>
        </div>

        {/* SEO-UI-95. **Deleted, because it had become false.** It read *"Nothing
            reaches your site yet — publishing … arrives in a later release"*, which
            was true while S-B28's five endpoints had no caller. `/seo/publish-targets`
            shipped in S-G5, and this same file's own note under the editor already
            links to it — so the gating screen was telling tenants the opposite of
            what the product does, on the one screen whose entire job is to be
            believed about what happens without asking. A warning that is wrong about
            the blast radius is worse than none: it invites somebody to switch on
            *Publish new blog posts automatically* on the understanding that nothing
            will. The blast-radius line beside each switch is the accurate statement
            and it is what stays. */}
        <InlineError failure={failure} />

        {/* SEO-UI-94. `.app-gate__row` rather than `.ar-panel.app-approve__row--top`.
            It is the port of the prototype's own `.sx-gate__row`, which is what S-D5
            draws these rows as — 0.75rem of padding at body-sm, against the panel's
            1.25rem at inherited 1rem — and it already carries the `flex-start` the
            drawing sets inline here, for the same reason: a three-line row with a
            switch centred against it floats the control away from the sentence it
            belongs to. The quality-gate lists on this screen and on the pipeline are
            the same class, so the three cannot drift apart. */}
        {CHANGES.map((change) => (
          <div key={change.code} className="app-gate__row">
            <label className="ar-switch">
              <input
                type="checkbox"
                checked={automatic[change.code] === true}
                aria-label={change.label}
                onChange={(event) => {
                  setAutomatic({ ...automatic, [change.code]: event.currentTarget.checked })
                  setFailure(null)
                }}
              />
              <span className="ar-switch__track" aria-hidden="true" />
            </label>
            <div className="app-gate__grow">
              <strong>{change.label}</strong>
              <span className="ar-subtle app-editor__block">{change.blast}</span>
            </div>
          </div>
        ))}

        <hr className="app-approve__rule" />

        <Field
          label="Who can approve"
          help="This can only narrow who may decide, never widen it. A separate Editor role is not built yet, so granting it would grant it to viewers too."
        >
          {(aria) => (
            <select
              className="ar-select"
              {...aria}
              value={role}
              onChange={(event) => {
                // **Narrowed rather than cast.** `approver_role` became a
                // two-value union when the API started refusing an unrecognised
                // role instead of silently defaulting it, and a `select` hands
                // back a plain `string`. Reading it through the option list is
                // what keeps the widening impossible on this side too: an
                // unexpected value leaves the floor where it was rather than
                // relaxing it to `admin`.
                const chosen = event.currentTarget.value
                if (chosen === 'owner' || chosen === 'admin') setRole(chosen)
                setFailure(null)
              }}
            >
              <option value="admin">Owner and Admin</option>
              <option value="owner">Owner only</option>
            </select>
          )}
        </Field>

        <Field
          label="If nobody approves within"
          help="We never auto-approve on a timeout. Drafts wait indefinitely if you want."
        >
          {(aria) => (
            <select
              className="ar-select"
              {...aria}
              value={String(stale)}
              onChange={(event) => {
                setStale(Number(event.currentTarget.value))
                setFailure(null)
              }}
            >
              {days.map((option) => (
                <option key={option} value={String(option)}>
                  {option === 0 ? 'Never expire' : `${String(option)} days — remind me again`}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
      <div className="ar-row" style={{ marginTop: 'var(--ar-space-4)' }}>
        <span className="ar-card__spacer" />
        <Button type="submit" tone="primary" busy={busy} busyLabel="Saving">
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

function ascending(a: number, b: number): number {
  return a - b
}
